import React, { useState, useEffect } from 'react';
import { Clock, Plus, Trash2, Edit, X, Car, ChevronDown, ChevronUp } from 'lucide-react';
import { BookingService } from '../../lib/bookings';
import { supabase } from '../../lib/supabase';
import { ServiceAddon } from '../../lib/services';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import EditServiceModal from './EditServiceModal';
import RichTextDescription from '../RichTextDescription';
import SelectServiceAddonsModal from '../manage/SelectServiceAddonsModal';

// 🚨 DEBUG LOGGER FOR EDIT SERVICE MODAL ISSUE 🚨
const logEditServiceDebug = (message: string, data?: any) => {
  console.log(`🚨 [ModifyBookingService] ${message}`, data || '');
  
  // Add to debug console if available
  if ((window as any).debugLogger) {
    (window as any).debugLogger.addLog('info', `🚨 [ModifyBookingService] ${message}`, data);
  }
};

interface ModifyBookingServiceProps {
  service: BookingService;
  bookingId: string;
  onUpdate: () => Promise<void>;
  onError: (error: string) => void;
  showSalesInfo?: boolean;
  canEdit?: boolean;
}

export default function ModifyBookingService({
  service,
  bookingId,
  onUpdate,
  onError,
  showSalesInfo = true,
  canEdit = true
}: ModifyBookingServiceProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSelectAddonsModalOpen, setIsSelectAddonsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Use showSalesInfo permission to control visibility of payment information
  const isPaymentsDisabled = !showSalesInfo;

  const handleEditService = async (updatedService: any) => {
    try {
      setLoading(true);

      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      console.log('[ModifyBookingService] Starting service update with data:', {
        serviceId: service.id,
        serviceName: updatedService.name,
        addonCount: updatedService.addons?.length || 0,
        originalAddonCount: service.addons?.length || 0
      });

      // Calculate tax amount based on updated service data with proper currency rounding
      const taxAmount = updatedService.tax_exempt ? 0 : Math.round((updatedService.price * (updatedService.tax_rate || 5) / 100) * 100) / 100;
      const totalAmount = Math.round((updatedService.price + taxAmount - (service.discount_amount || 0)) * 100) / 100;

      // Create a comprehensive update payload with all needed fields
      const updatePayload = {
        name: updatedService.name.trim(),
        description: updatedService.description?.trim() || null,
        price: updatedService.price,
        duration_minutes: updatedService.duration_minutes,
        tax_exempt: updatedService.tax_exempt,
        tax_rate: updatedService.tax_exempt ? 0 : (updatedService.tax_rate || 5),
        tax_amount: taxAmount,
        total_amount: totalAmount,
        updated_at: new Date().toISOString()
      };

      console.log('[ModifyBookingService] Sending update to database:', {
        serviceId: service.id,
        updateFields: Object.keys(updatePayload)
      });

      // First step: Update the main service data
      const { data, error } = await supabase
        .from('booking_services')
        .update(updatePayload)
        .eq('id', service.id)
        .select('*')
        .single();

      if (error) {
        console.error('[ModifyBookingService] Database update error:', {
          error,
          serviceId: service.id
        });
        throw error;
      }

      // Second step: Handle addons if they exist
      if (updatedService.addons) {
        // Log details about the addons we're going to process
        console.log('[ModifyBookingService] Processing service add-ons:', {
          serviceId: service.id,
          addonCount: updatedService.addons.length,
          addonIds: updatedService.addons.map((a: any) => a.id).join(', ')
        });
        
        try {
          // Delete existing add-ons first
          console.log(`[ModifyBookingService] Deleting existing add-ons for service ID ${service.id}`);
          const { error: deleteError } = await supabase
            .from('booking_service_addons')
            .delete()
            .eq('booking_service_id', service.id);

          if (deleteError) {
            console.error('[ModifyBookingService] Error deleting existing add-ons:', deleteError);
            throw deleteError;
          }

          // Insert new add-ons if any
          if (updatedService.addons.length > 0) {
            const timestamp = new Date().toISOString();
            
            const addonInserts = updatedService.addons.map((addon: any) => {
              // Make sure addon has an ID
              if (!addon.id) {
                console.warn('[ModifyBookingService] Addon missing ID, generating temporary one');
                addon.id = 'temp-' + Math.random().toString(36).substring(2, 9);
              }
              
              const addonInsert = {
                booking_service_id: service.id,
                addon_id: addon.id,
                price: addon.price || 0,
                created_at: timestamp,
                updated_at: timestamp
              };
              
              console.log('[ModifyBookingService] Preparing addon insert:', {
                addonId: addon.id,
                addonName: addon.name,
                price: addon.price
              });
              
              return addonInsert;
            });

            console.log('[ModifyBookingService] Inserting addons:', {
              count: addonInserts.length,
              serviceId: service.id
            });
            
            const { error: insertError } = await supabase
              .from('booking_service_addons')
              .insert(addonInserts);

            if (insertError) {
              console.error('[ModifyBookingService] Error inserting new add-ons:', {
                error: insertError,
                insertData: addonInserts
              });
              throw insertError;
            }
            
            console.log('[ModifyBookingService] Successfully inserted addons');
          }
        } catch (addonError) {
          console.error('[ModifyBookingService] Error updating add-ons:', addonError);
          // Continue without throwing - we'll still update the main service
        }
      }

      console.log('[ModifyBookingService] Database update successful');

      // Note: Removed PDF generation code - no longer using the old invoicing system

      // Refresh the booking data
      console.log('[ModifyBookingService] Refreshing booking data');
      await onUpdate();

      console.log('[ModifyBookingService] Update complete, closing modal');
      setIsEditModalOpen(false);
    } catch (err) {
      console.error('[ModifyBookingService] Error in service update:', {
        error: err,
        service: service.id
      });
      onError(err instanceof Error ? err.message : 'Failed to update service');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveService = async () => {
    try {
      setLoading(true);

      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      // Clean up workflows for this service before deleting
      try {
        const { deleteBookingWorkflowsByServiceId } = await import('../../lib/bookingWorkflows');
        await deleteBookingWorkflowsByServiceId(service.booking_id, service.service_id);
        console.log(`[WORKFLOW CLEANUP] Cleaned up workflows for service ${service.service_id} in booking ${service.booking_id}`);
      } catch (workflowCleanupError) {
        console.error('[WORKFLOW CLEANUP] Error cleaning up workflows:', workflowCleanupError);
        // Don't throw here - continue with service deletion even if workflow cleanup fails
      }

      // Delete the service directly
      const { error } = await supabase
        .from('booking_services')
        .delete()
        .eq('id', service.id);
      
      if (error) throw error;

      // Note: Removed PDF generation code - no longer using the old invoicing system

      await onUpdate();
    } catch (err) {
      console.error('Error removing service:', err);
      onError(err instanceof Error ? err.message : 'Failed to remove service');
    } finally {
      setLoading(false);
    }
  };

  const handleAddonsSelect = async (addons: ServiceAddon[]) => {
    try {
      setLoading(true);

      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      console.log('Starting add-ons update:', { 
        serviceId: service.id,
        bookingId: service.booking_id,
        addonsToAdd: addons 
      });

      // Delete existing add-ons
      const { error: deleteError } = await supabase
        .from('booking_service_addons')
        .delete()
        .eq('booking_service_id', service.id);

      if (deleteError) {
        console.error('Error deleting existing add-ons:', deleteError);
        throw deleteError;
      }

      // Insert new add-ons if any
      if (addons.length > 0) {
        const { error: insertError } = await supabase
          .from('booking_service_addons')
          .insert(
            addons.map((addon) => ({
              booking_service_id: service.id,
              addon_id: addon.id,
              price: addon.price
            }))
          );

        if (insertError) {
          console.error('Error inserting new add-ons:', insertError);
          throw insertError;
        }
      }

      console.log('Successfully updated add-ons');

      await onUpdate();
      setIsSelectAddonsModalOpen(false);
    } catch (err) {
      console.error('Error updating add-ons:', err);
      onError(err instanceof Error ? err.message : 'Failed to update add-ons');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAddon = async (addonId: string) => {
    try {
      setLoading(true);

      if (!supabase) {
        throw new Error('Supabase client is not initialized');
      }

      // Delete the specific add-on
      const { error: deleteError } = await supabase
        .from('booking_service_addons')
        .delete()
        .eq('booking_service_id', service.id)
        .eq('addon_id', addonId);

      if (deleteError) {
        console.error('Error deleting add-on:', deleteError);
        throw deleteError;
      }

      await onUpdate();
    } catch (err) {
      console.error('Error removing add-on:', err);
      onError(err instanceof Error ? err.message : 'Failed to remove add-on');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900">{service.name}</div>
          <RichTextDescription description={service.description} className="text-sm text-gray-500" />
          <div className="flex items-center mt-2 text-xs text-gray-500">
            <Clock className="h-3.5 w-3.5 mr-1" />
            {service.duration_minutes >= 1440 
              ? `${Math.round(service.duration_minutes / 1440)} days`
              : `${Math.round(service.duration_minutes / 60)} hours`
            }
          </div>
          {/* Display add-ons if any */}
          {service.addons && service.addons.length > 0 && (
            <div className="mt-2">
              <div className="text-xs font-medium text-gray-500">Add-ons:</div>
              <div className="space-y-1 mt-1">
                {service.addons.map((addon) => (
                  <div key={addon.id} className="flex items-center justify-between text-xs">
                    <div className="flex-1">
                      <span className="text-gray-600">{addon.name}</span>
                      <div className="flex items-center mt-0.5 text-gray-500">
                        <Clock className="h-3 w-3 mr-1" />
                        {addon.duration_minutes >= 1440 
                          ? `${Math.round(addon.duration_minutes / 1440)} days`
                          : `${Math.round(addon.duration_minutes / 60)} hours`
                        }
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {!isPaymentsDisabled && (
                        <span className="text-gray-900">${addon.price.toLocaleString()}</span>
                      )}
                      {canEdit && (
                        <button
                          onClick={() => handleRemoveAddon(addon.id)}
                          disabled={loading}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {canEdit && (
            <div className="flex items-center space-x-3 mt-2">
              <button
                onClick={() => {
                  // 🚨 DEBUG LOGGING FOR EDIT SERVICE MODAL ISSUE 🚨
                  logEditServiceDebug('🔧 EDIT BUTTON CLICKED - About to open EditServiceModal', {
                    serviceId: service.id,
                    serviceName: service.name,
                    bookingId: bookingId,
                    timestamp: new Date().toISOString(),
                    windowWidth: window.innerWidth,
                    windowHeight: window.innerHeight,
                    userAgent: navigator.userAgent,
                    isBookingModalOpen: !!document.querySelector('.booking-info-modal'),
                    bookingModalElement: document.querySelector('.booking-info-modal')?.className || 'NOT FOUND'
                  });

                  // Check current DOM state before opening modal
                  setTimeout(() => {
                    logEditServiceDebug('🔍 DOM STATE AFTER EDIT MODAL OPENS', {
                      bookingModal: {
                        found: !!document.querySelector('.booking-info-modal'),
                        visible: document.querySelector('.booking-info-modal') ? 
                          window.getComputedStyle(document.querySelector('.booking-info-modal')!).display !== 'none' : false,
                        zIndex: document.querySelector('.booking-info-modal') ? 
                          window.getComputedStyle(document.querySelector('.booking-info-modal')!).zIndex : 'N/A'
                      },
                      editServiceModal: {
                        found: !!document.querySelector('.edit-service-modal'),
                        visible: document.querySelector('.edit-service-modal') ? 
                          window.getComputedStyle(document.querySelector('.edit-service-modal')!).display !== 'none' : false,
                        zIndex: document.querySelector('.edit-service-modal') ? 
                          window.getComputedStyle(document.querySelector('.edit-service-modal')!).zIndex : 'N/A'
                      },
                      modalNesting: document.querySelector('.booking-info-modal') && document.querySelector('.edit-service-modal') ?
                        document.querySelector('.booking-info-modal')!.contains(document.querySelector('.edit-service-modal')!) : false
                    });
                  }, 100);

                  setIsEditModalOpen(true);
                }}
                disabled={loading || service.status === 'cancelled'}
                className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                Edit
              </button>
              <button
                onClick={() => setIsSelectAddonsModalOpen(true)}
                disabled={loading || service.status === 'cancelled'}
                className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                Add-ons
              </button>
              <button
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={loading || service.status === 'cancelled'}
                className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
              >
                Remove
              </button>
            </div>
          )}
        </div>
        {/* Only show price if payments are not disabled */}
        {!isPaymentsDisabled && (
          <div className="text-sm font-medium text-gray-900 ml-4 flex-shrink-0">
            ${service.price.toLocaleString()}
          </div>
        )}
      </div>

      {/* 🚀 FIX: Use React Portal to render EditServiceModal outside parent DOM tree */}
      {isEditModalOpen && (
        <EditServiceModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          service={{
            name: service.name || '',
            description: service.description || '',
            price: service.price || 0,
            duration_minutes: service.duration_minutes || 0,
            is_active: true,
            tax_rate: service.tax_rate || 5,
            tax_exempt: service.tax_exempt || false,
            addons: Array.isArray(service.addons) ? service.addons.map(addon => ({
              id: addon.id,
              name: addon.name || '',
              description: addon.description || '',
              price: addon.price || 0,
              duration_minutes: addon.duration_minutes || 0,
              order_index: addon.order_index || 0,
              service_id: service.service_id || '',
              addon_service_id: addon.addon_service_id || addon.id || '',
              created_at: addon.created_at || new Date().toISOString(),
              updated_at: addon.updated_at || new Date().toISOString()
            })) : []
          }}
          onSave={handleEditService}
        />
      )}

      <SelectServiceAddonsModal
        isOpen={isSelectAddonsModalOpen}
        onClose={() => setIsSelectAddonsModalOpen(false)}
        onSelect={handleAddonsSelect}
        serviceId={service.service_id}
        selectedAddons={service.addons || []}
      />
      
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={async () => {
          try {
            setDeleteLoading(true);
            await handleRemoveService();
            setIsDeleteModalOpen(false);
          } finally {
            setDeleteLoading(false);
          }
        }}
        title="Remove Service"
        message="Are you sure you want to remove this service? This action cannot be undone."
        loading={deleteLoading}
      />
    </>
  );
}